import { useCallback, useMemo } from 'react';

import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import useUser from '@/hooks/swr/useUser';
import useSubscription from '@/utils/hooks/swr/pricing/useSubscription';
// Hooks
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import type { SocialProfileType } from '@/types/socialProfileType';

dayjs.extend(utc);
dayjs.extend(timezone);

const useDateLimits = () => {
    const { activeSocialProfile } = useActiveSocialProfile();
    const { subscription } = useSubscription();

    const { user } = useUser();

    const getDataRetentionDate = useCallback(() => {
        if (subscription?.data_retention_date) {
            return dayjs(subscription.data_retention_date)
                .tz(user?.settings.timezone)
                .startOf('day')
                .format('YYYY-MM-DDTHH:mm:ss');
        }
        // return fallback
        return dayjs()
            .tz(user?.settings.timezone)
            .subtract(15, 'years')
            .startOf('day')
            .format('YYYY-MM-DDTHH:mm:ss');
    }, [subscription?.data_retention_date, user?.settings.timezone]);

    const getMinDate = useCallback(
        (socialProfile?: SocialProfileType) => {
            // can be removed after youtube review
            if (activeSocialProfile?.social_network_type === 'youtube') {
                return dayjs(activeSocialProfile.min_date_stats)
                    .tz(user?.settings.timezone)
                    .startOf('day')
                    .format('YYYY-MM-DDTHH:mm:ss');
            }

            // check if there is a data retention date
            if (subscription?.data_retention_date) {
                if (socialProfile?.min_date_stats) {
                    const retentionBeforeMinDate = dayjs(subscription.data_retention_date)
                        .tz(user.settings.timezone)
                        .isBefore(dayjs(socialProfile.min_date_stats).tz(user.settings.timezone));

                    // if the retention date is before the min date, we use the min date
                    if (retentionBeforeMinDate) {
                        return dayjs(socialProfile.min_date_stats)
                            .tz(user?.settings.timezone)
                            .startOf('day')
                            .format('YYYY-MM-DDTHH:mm:ss');
                    }
                }

                return dayjs(subscription.data_retention_date)
                    .tz(user?.settings.timezone)
                    .startOf('day')
                    .format('YYYY-MM-DDTHH:mm:ss');
            }

            if (socialProfile?.min_date_stats) {
                return dayjs(socialProfile.min_date_stats)
                    .tz(user?.settings.timezone)
                    .startOf('day')
                    .format('YYYY-MM-DDTHH:mm:ss');
            }

            return dayjs()
                .tz(user?.settings.timezone)
                .subtract(15, 'years')
                .startOf('day')
                .format('YYYY-MM-DDTHH:mm:ss');
        },
        [activeSocialProfile, subscription?.data_retention_date, user?.settings.timezone]
    );

    const getMaxDate = useCallback(
        (socialProfile?: SocialProfileType) => {
            if (socialProfile?.social_network_type === 'facebook') {
                return dayjs()
                    .tz(user?.settings.timezone)
                    .subtract(1, 'days')
                    .endOf('day')
                    .format('YYYY-MM-DDTHH:mm:ss');
            }

            return dayjs()
                .tz(user?.settings.timezone)
                .endOf('day')
                .format('YYYY-MM-DDTHH:mm:ss');
        },
        [user?.settings?.timezone]
    );

    const minDate = useMemo(
        () => getMinDate(activeSocialProfile),
        [activeSocialProfile, getMinDate]
    );

    const maxDate = useMemo(
        () => getMaxDate(activeSocialProfile),
        [activeSocialProfile, getMaxDate]
    );

    return { minDate, maxDate, getMaxDate, getMinDate, getDataRetentionDate };
};

export default useDateLimits;
